import request from '@/utils/request';

/**
 * 角色权限控制工具（前端硬编码角色，与后端角色表 roleCode 字段对齐）。
 *
 * 设计原则：
 * 1. 角色编码与后端 admin_role.roleCode 完全一致；SUPER_ADMIN 默认全权限，
 *    其余角色按硬编码的菜单白名单控制。
 * 2. 历史账号未配置任何角色时，降级为"全权限"（与改造前一致），避免误锁。
 * 3. 角色信息通过登录后调用 `/admin/account/detail/{adminId}` 现成接口获取，
 *    后端在 `AdminUserPageVO.roleCode` 字段直接返回当前账号的角色编码列表，
 *    **不需要**额外的 2 跳（不再调 `/admin/role/options`）。
 * 4. 角色信息缓存到 localStorage，登录后刷新页面继续生效；切换账号登出时清空。
 *
 * 角色与菜单权限矩阵：
 * - SUPER_ADMIN：全部菜单
 * - OPERATION：首页、产品管理、订单管理、出行管理、轮播图管理、数据统计、短信模板管理、FAQ管理、系统设置
 * - CS：订单管理（+首页/，首页属于"所有角色公共可见"）
 *
 * 公共菜单（COMMON_MENUS）：所有已配置角色以及未配置角色的降级账号都可见，
 * 与角色白名单独立判断；目前包含首页 /index。
 */

export const ROLE = {
    SUPER_ADMIN: 'SUPER_ADMIN',
    OPERATION: 'OPERATION',
    CS: 'CS',
} as const;

export type RoleCode = (typeof ROLE)[keyof typeof ROLE];

/** 所有角色公共可见的菜单（与角色白名单独立，命中即放行）。 */
const COMMON_MENUS: readonly string[] = ['/index'];

/** 各角色允许的菜单路径集合（SUPER_ADMIN 不在此列出，等价于全放行）。 */
const ROLE_MENUS: Record<RoleCode, readonly string[]> = {
    [ROLE.OPERATION]: [
        '/index',
        '/productManagement',
        '/orderManagement',
        '/travelManagement',
        '/travelManagementV2',
        '/bannerManagement',
        '/dataStatistics',
        '/smsTemplate',
        '/faqManagement',
        '/idCardBlacklist',
        '/settings',
    ],
    [ROLE.CS]: ['/orderManagement', '/travelManagement', '/travelManagementV2'],
};

/** CS 角色在订单管理页受限的按钮（per-order + 批量）。 */
export const CS_RESTRICTED_ORDER_BUTTONS = {
    /** 单条：出票 */
    issue: true,
    /** 单条：退款 */
    refund: true,
    /** 单条：退款审核 */
    refundAudit: true,
    /** 单条：修改订单 */
    modify: true,
    /** 单条：发送短信 */
    sendSms: true,
    /** 单条：发送退款短信 */
    sendRefundSms: true,
    /** 单条：取消订单 */
    cancel: true,
    /** 批量：出票 */
    batchIssue: true,
    /** 批量：退款 */
    batchRefund: true,
    /** 批量：查看汇总 */
    summary: true,
    /** 批量：导出订单 */
    export: true,
} as const;

export type OrderButtonKey = keyof typeof CS_RESTRICTED_ORDER_BUTTONS;

const ROLE_CODES_STORAGE_KEY = 'roleCodes';

/**
 * 从 localStorage 读取当前登录用户的角色编码列表。
 * 解析失败或未登录时返回空数组。
 */
export const getRoleCodes = (): RoleCode[] => {
    try {
        const raw = localStorage.getItem(ROLE_CODES_STORAGE_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];
        // 过滤掉非预期值，避免污染判断
        return parsed.filter(
            (v): v is RoleCode =>
                v === ROLE.SUPER_ADMIN || v === ROLE.OPERATION || v === ROLE.CS,
        );
    } catch {
        return [];
    }
};

/**
 * 是否超级管理员（含历史无角色账号的降级放行）。
 * 任意一个角色为 SUPER_ADMIN 即视为全权限；角色列表为空也视为全权限。
 */
export const isSuperAdmin = (): boolean => {
    const codes = getRoleCodes();
    if (codes.length === 0) return true;
    return codes.includes(ROLE.SUPER_ADMIN);
};

/**
 * 菜单/路由是否对当前用户可见。
 * 命中顺序：SUPER_ADMIN/无角色账号 → 公共菜单 → 角色白名单并集。
 */
export const canAccessMenu = (path: string): boolean => {
    if (isSuperAdmin()) return true;
    if (COMMON_MENUS.includes(path)) return true;
    const codes = getRoleCodes();
    return codes.some((code) => (ROLE_MENUS[code] ?? []).includes(path));
};

/**
 * 订单管理页按钮是否对当前用户可见。
 * 非 CS 用户不受限；CS 受限按钮全部隐藏；SUPER_ADMIN / 无角色账号全放行。
 */
export const canShowOrderButton = (key: OrderButtonKey): boolean => {
    if (isSuperAdmin()) return true;
    const codes = getRoleCodes();
    if (!codes.includes(ROLE.CS)) return true;
    return !CS_RESTRICTED_ORDER_BUTTONS[key];
};

/**
 * 登出/切换账号时清空角色缓存。
 * 与 auth.ts 的 logout 行为保持一致——清除 token / userInfo 之外再清角色。
 */
export const clearRoleCodes = (): void => {
    localStorage.removeItem(ROLE_CODES_STORAGE_KEY);
};

interface AccountDetailVO {
    roleIds?: number[];
    roleNames?: string[];
    /** 后端 AdminUserPageVO.roleCode 字段（List<String>，命名虽是单数但值为列表） */
    roleCode?: string[];
}

/**
 * 加载当前登录用户的角色编码并写入 localStorage。
 *
 * 后端已在 `/admin/account/detail/{adminId}` 返回的 `AdminUserPageVO.roleCode`
 * 字段中直接给出当前账号的角色编码列表（如 ["CS"] / ["OPERATION"] / ["SUPER_ADMIN"]），
 * 一次请求即可拿到所有需要的角色编码，不再需要二次反查。
 *
 * 失败 / 未登录 / 账号无角色：清空缓存，视为无角色账号走全权限降级。
 */
export const loadCurrentUserRoles = async (
    adminId: number,
): Promise<RoleCode[]> => {
    if (!adminId) {
        clearRoleCodes();
        return [];
    }
    try {
        const detail = await request.get<any, AccountDetailVO>(
            `/admin/account/detail/${adminId}`,
        );

        const codes: RoleCode[] = [];
        (detail?.roleCode || []).forEach((code) => {
            if (
                (code === ROLE.SUPER_ADMIN ||
                    code === ROLE.OPERATION ||
                    code === ROLE.CS) &&
                !codes.includes(code)
            ) {
                codes.push(code);
            }
        });

        localStorage.setItem(ROLE_CODES_STORAGE_KEY, JSON.stringify(codes));
        return codes;
    } catch (err) {
        // 失败时清空缓存，避免脏数据；按"无角色=全权限"降级
        clearRoleCodes();
        return [];
    }
};
